import {H3Text, H6Text} from '@buncha/components/core/text';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {AOOSShopperStatus} from '@buncha/batchDetails/types';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import Box from '@buncha/components/core/view/Box';
import CircleoutlinedIcon from '@buncha/components/core/icon/CircleOutlinedIcon';
import {HBox} from '@buncha/components/core/view';
import HourTimer from '@buncha/components/core/icon/HourTimer';
import If from '@buncha/components/conditional/If';
import LockClockIcon from '@buncha/components/core/icon/LockClockIcon';
import {Modules} from '@buncha/utils/types';
import PeopleChatIcon from '@buncha/components/core/icon/PeopleChatIcon';
import PickingIcon from '@buncha/components/core/icon/PickingIcon';
import React from 'react';
import RoundedTickIcon from '@buncha/components/core/icon/RoundedTickIcon';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {Skeleton} from 'native-base';
import {StyleSheet} from 'react-native';
import {useGetLocale} from '@buncha/localization/hooks';

interface CustomerResponseSheetProps {
  open: boolean;
  timer?: string;
  status?: AOOSShopperStatus;
  navigateToBatchDetail: () => void;
}
const CustomerResponseSheet: React.FC<CustomerResponseSheetProps> = props => {
  const {status} = props;
  const theme = useColorThemeType();
  const muliFont = useFontTheme('muli');
  const mortiseFont = useFontTheme('mortise');
  const {title, body, button} =
    useGetLocale(Modules.itemDetails, ShopperScreens.ItemDetails) ?? {};

  const hasCustomerResponded =
    status === AOOSShopperStatus.customerApproved ||
    status === AOOSShopperStatus.itemPicked;

  return (
    <BottomSheet open={props.open}>
      <Box style={styles.boxContainer}>
        <LockClockIcon style={styles.lockIcon} height={48} width={48} />
        <Box style={styles.titleBox}>
          <H3Text
            style={[
              styles.text,
              {
                color: theme?.text.blue[300],
                fontFamily: mortiseFont[700].normal,
              },
            ]}>
            {title?.customerResponse}
          </H3Text>
          <Box style={styles.statusCellContainer}>
            <HBox style={styles.statusCell}>
              <PeopleChatIcon
                fill={theme?.text?.blue?.[300]}
                width={24}
                height={24}
              />
              <HBox style={styles.statusCellInnerBox}>
                <H6Text
                  style={{
                    color: theme?.text?.blue?.[300],
                  }}>
                  {body?.customersApproval}
                </H6Text>
                <If condition={hasCustomerResponded}>
                  <RoundedTickIcon
                    height={22}
                    viewBox="0 0 24 24"
                    width={22}
                    fill={theme?.icon?.green?.[100]}
                  />
                </If>
                <If condition={!hasCustomerResponded}>
                  <CircleoutlinedIcon
                    width={30}
                    height={30}
                    fill={theme?.background?.white?.[100]}
                  />
                </If>
              </HBox>
            </HBox>
            <HBox style={styles.statusCell}>
              <PickingIcon
                fill={
                  hasCustomerResponded
                    ? theme?.text?.blue?.[300]
                    : theme?.text?.gray?.[100]
                }
                viewBox="0 0 24 24"
                width={24}
                height={24}
              />
              <HBox style={styles.statusCellInnerBox}>
                <H6Text
                  style={{
                    color: hasCustomerResponded
                      ? theme?.text?.blue?.[300]
                      : theme?.text?.gray?.[100],
                  }}>
                  {body?.itemToBePicked}
                </H6Text>
                <CircleoutlinedIcon
                  width={30}
                  height={30}
                  fill={theme?.background?.white?.[100]}
                />
              </HBox>
            </HBox>
          </Box>
        </Box>
        <Box style={[styles.timerBox, {borderColor: theme?.text?.gray?.[200]}]}>
          <HBox>
            <H6Text
              style={[
                styles.text,
                {
                  color: theme?.text.gray[100],
                  fontFamily: muliFont[600].normal,
                },
              ]}>
              {body?.waitFor}
            </H6Text>
            <HBox>
              <HourTimer
                fill={theme?.text.gray[100]}
                height={28}
                width={28}
                viewBox="0 -4 28 28"
              />
              <If condition={props?.timer}>
                <H6Text
                  style={[
                    styles.text,
                    {
                      color: theme?.text.gray[100],
                      fontFamily: muliFont[600].normal,
                    },
                  ]}>
                  {props?.timer}
                </H6Text>
              </If>
              <If condition={!props?.timer}>
                <Skeleton style={styles.skeleton} />
              </If>
            </HBox>
          </HBox>
        </Box>
        <BottomButton
          style={[styles.buttonContainer]}
          title={button?.continueShopping}
          onPress={props?.navigateToBatchDetail}
        />
      </Box>
    </BottomSheet>
  );
};

export default React.memo(CustomerResponseSheet);

const styles = StyleSheet.create({
  boxContainer: {
    width: '95%',
  },
  statusCell: {
    justifyContent: 'flex-start',
    gap: 5,
  },
  statusCellInnerBox: {
    flex: 1,
    justifyContent: 'space-between',
  },
  statusCellContainer: {
    gap: 10,
  },
  buttonContainer: {
    width: '100%',
    marginTop: 10,
  },
  text: {
    textAlign: 'left',
  },
  skeleton: {
    width: 70,
    height: 25,
    borderRadius: 8,
  },
  leftButton: {
    flex: 1,
    marginRight: 5,
    borderWidth: 1,
  },
  rightButton: {
    flex: 1,
    marginLeft: 5,
  },
  titleBox: {
    marginTop: 20,
    marginBottom: 10,
    gap: 10,
  },
  timerBox: {
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderRadius: 5,
  },
  buttonBox: {
    marginVertical: 20,
  },
  lockIcon: {alignSelf: 'center'},
});
